// src/services/d1Api.js
// Cloudflare D1 Database API Service - Dr. Hamdy Saleh Platform
// All paths match the actual worker.js Hono routes (NO /api/ prefix)

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
    const token = localStorage.getItem('token');

    const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers
    };

    const config = {
        ...options,
        headers
    };

    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || data.message || 'API call failed');
        }

        return { success: true, data: data.data !== undefined ? data.data : data };
    } catch (error) {
        console.error(`API Error (${endpoint}):`, error);
        return { success: false, error: error.message };
    }
}

// ============= COURSES =============
// Worker: GET /courses  (optional ?grade=)
export const getSuggestedCourses = async (grade) => {
    const query = grade ? `?grade=${grade}` : '';
    return apiCall(`/courses${query}`);
};

export const getAllCourses = async () => {
    return apiCall('/courses');
};

/** Alias used by AdminDashboard */
export const getCourses = getAllCourses;

export const getCourseById = async (courseId) => {
    return apiCall(`/courses/${courseId}`);
};

// Worker: POST /courses
export const createCourse = async (courseData) => {
    return apiCall('/courses', {
        method: 'POST',
        body: JSON.stringify(courseData)
    });
};

export const updateCourse = async (courseId, courseData) => {
    return apiCall(`/courses/${courseId}`, {
        method: 'PUT',
        body: JSON.stringify(courseData)
    });
};

// Worker: DELETE /courses/:id
export const deleteCourse = async (courseId) => {
    return apiCall(`/courses/${courseId}`, { method: 'DELETE' });
};

// Worker: POST /enrollments
export const enrollInCourse = async (courseId, userId) => {
    return apiCall('/enrollments', {
        method: 'POST',
        body: JSON.stringify({ course_id: courseId, user_id: userId })
    });
};

// ============= LECTURES =============
// Worker: GET /lectures  (optional ?course_id=)
export const getLectures = async (courseId) => {
    const query = courseId ? `?course_id=${courseId}` : '';
    return apiCall(`/lectures${query}`);
};

export const getLectureById = async (lectureId) => {
    return apiCall(`/lectures/${lectureId}`);
};

// Worker: POST /lectures
export const createLecture = async (lectureData) => {
    return apiCall('/lectures', {
        method: 'POST',
        body: JSON.stringify(lectureData)
    });
};

// Worker: PUT /lectures/:id
export const updateLecture = async (lectureId, lectureData) => {
    return apiCall(`/lectures/${lectureId}`, {
        method: 'PUT',
        body: JSON.stringify(lectureData)
    });
};

export const deleteLecture = async (lectureId) => {
    return apiCall(`/lectures/${lectureId}`, { method: 'DELETE' });
};

// ============= PAYMENTS =============
// Worker: POST /payment-requests
export const createPaymentRequest = async (paymentData) => {
    return apiCall('/payment-requests', {
        method: 'POST',
        body: JSON.stringify(paymentData)
    });
};

// Worker: GET /payment-requests  (optional ?status=)
export const getPaymentRequests = async (status) => {
    const query = status ? `?status=${status}` : '';
    return apiCall(`/payment-requests${query}`);
};

// Worker: PUT /payment-requests/:id
export const updatePaymentRequest = async (paymentId, updateData) => {
    return apiCall(`/payment-requests/${paymentId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
    });
};

export const updatePaymentStatus = async (paymentId, status) => {
    return apiCall(`/payment-requests/${paymentId}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
    });
};

export const verifyPayment = async (paymentId) => {
    return apiCall(`/payment-requests/${paymentId}`);
};

// ============= USER PROFILES =============
// Worker: GET /profiles/:id
export const getProfile = async (userId) => {
    if (userId) return apiCall(`/profiles/${userId}`);
    const id = localStorage.getItem('userId');
    if (id) return apiCall(`/profiles/${id}`);
    return { success: false, error: 'No userId' };
};

// Worker: PUT /profiles/:id
export const updateProfile = async (profileData) => {
    const id = localStorage.getItem('userId');
    if (!id) return { success: false, error: 'No userId' };
    return apiCall(`/profiles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(profileData)
    });
};

// Admin: update any user's profile by ID
export const adminUpdateProfile = async (userId, profileData) => {
    return apiCall(`/profiles/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(profileData)
    });
};

// Login with device switch (force replace old device)
export const switchDeviceLogin = async (credentials) => {
    return apiCall('/login/switch-device', {
        method: 'POST',
        body: JSON.stringify(credentials)
    });
};

// Worker: GET /profiles  (optional ?role=)
/** Get all profiles, optionally filtered by role */
export const getProfiles = async (role) => {
    const query = role ? `?role=${role}` : '';
    return apiCall(`/profiles${query}`);
};

// Worker: PUT /users/:id/status
export const updateUserStatus = async (userId, status) => {
    return apiCall(`/users/${userId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ accountStatus: status })
    });
};

// Worker: DELETE /users/:id
export const deleteUser = async (userId) => {
    return apiCall(`/users/${userId}`, { method: 'DELETE' });
};

// Worker: PUT /users/:id/kick  (soft-delete: sets accountStatus to 'kicked')
export const kickUser = async (userId) => {
    return apiCall(`/users/${userId}/kick`, { method: 'PUT' });
};

// Worker: POST /messages  (body: { user_id, admin_id, content })
export const sendMessageToStudent = async (userId, message) => {
    return apiCall('/messages', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, content: message })
    });
};

// Worker: GET /messages?user_id=  (student reads admin messages)
export const getStudentMessages = async (userId) => {
    return apiCall(`/messages?user_id=${userId}`);
};

// ============= SUBSCRIPTIONS =============
export const getSubscriptions = async () => {
    return apiCall('/subscriptions');
};

export const createSubscription = async (subscriptionData) => {
    return apiCall('/subscriptions', {
        method: 'POST',
        body: JSON.stringify(subscriptionData)
    });
};

// ============= QUIZZES =============
// Worker: GET /quizzes  (optional ?course_id=)
export const getQuizzes = async (courseId) => {
    return apiCall(`/quizzes${courseId ? `?course_id=${courseId}` : ''}`);
};

/** Alias: get all quizzes without filter */
export const getAllQuizzes = async () => {
    return apiCall('/quizzes');
};

export const getQuizById = async (quizId) => {
    return apiCall(`/quizzes/${quizId}`);
};

// Worker: POST /quizzes
export const createQuiz = async (quizData) => {
    return apiCall('/quizzes', {
        method: 'POST',
        body: JSON.stringify(quizData)
    });
};

export const updateQuiz = async (quizId, quizData) => {
    return apiCall(`/quizzes/${quizId}`, {
        method: 'PUT',
        body: JSON.stringify(quizData)
    });
};

export const deleteQuiz = async (quizId) => {
    return apiCall(`/quizzes/${quizId}`, { method: 'DELETE' });
};

// Worker: POST /quiz-questions
export const createQuizQuestion = async (questionData) => {
    return apiCall('/quiz-questions', {
        method: 'POST',
        body: JSON.stringify(questionData)
    });
};

// Worker: GET /quiz-questions?quiz_id=
export const getQuizQuestions = async (quizId) => {
    return apiCall(`/quiz-questions?quiz_id=${quizId}`);
};

// Worker: DELETE /quiz-questions/:id
export const deleteQuizQuestion = async (questionId) => {
    return apiCall(`/quiz-questions/${questionId}`, { method: 'DELETE' });
};

export const submitQuiz = async (quizId, answers) => {
    return apiCall(`/quizzes/${quizId}/submit`, {
        method: 'POST',
        body: JSON.stringify({ answers })
    });
};

// ============= PROGRESS =============
export const getUserProgress = async () => {
    return apiCall('/progress');
};

export const updateProgress = async (progressData) => {
    return apiCall('/progress', {
        method: 'PUT',
        body: JSON.stringify(progressData)
    });
};

// ============= LEADERBOARD =============
export const getLeaderboard = async (grade) => {
    const query = grade ? `?grade=${grade}` : '';
    return apiCall(`/leaderboard${query}`);
};

// ============= ANNOUNCEMENTS =============
// Worker: GET /announcements
export const getAnnouncements = async () => {
    return apiCall('/announcements');
};

export const createAnnouncement = async (announcementData) => {
    return apiCall('/announcements', {
        method: 'POST',
        body: JSON.stringify(announcementData)
    });
};

export const deleteAnnouncement = async (announcementId) => {
    return apiCall(`/announcements/${announcementId}`, { method: 'DELETE' });
};

// ============= FORUM =============
// Worker: GET /forum-posts  (optional ?status=)
export const getForumPosts = async (status) => {
    const query = status ? `?status=${status}` : '';
    return apiCall(`/forum-posts${query}`);
};

// Worker: GET /forum-posts?user_id=  (returns own posts including pending + approved from others)
export const getForumPostsForUser = async (userId) => {
    return apiCall(`/forum-posts?user_id=${userId}`);
};

// Worker: POST /forum-posts
export const createForumPost = async (postData) => {
    return apiCall('/forum-posts', {
        method: 'POST',
        body: JSON.stringify(postData)
    });
};

// Worker: PUT /forum-posts/:id  (body: { status })
export const updateForumPostStatus = async (postId, status) => {
    return apiCall(`/forum-posts/${postId}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
    });
};

export const deleteForumPost = async (postId) => {
    return apiCall(`/forum-posts/${postId}`, { method: 'DELETE' });
};

// Worker: PUT /users/:id/forum-block
export const toggleForumBlock = async (userId, isBlocked) => {
    return apiCall(`/users/${userId}/forum-block`, {
        method: 'PUT',
        body: JSON.stringify({ is_blocked_from_forum: isBlocked })
    });
};

// Worker: PUT /forum-posts/:id/reply
export const replyToForumPost = async (postId, reply) => {
    return apiCall(`/forum-posts/${postId}/reply`, {
        method: 'PUT',
        body: JSON.stringify({ admin_reply: reply })
    });
};

// ============= CENTER CODES =============
// Worker: GET /center-codes
export const getCenterCodes = async () => {
    return apiCall('/center-codes');
};

// Worker: POST /center-codes/package  (admin create)
// FIX: Always sends expires_at (not expiryDate) to align with the worker's canonical column name.
export const createCenterCode = async (codeData) => {
    return apiCall('/center-codes/package', {
        method: 'POST',
        body: JSON.stringify(codeData)
    });
};

// Worker: GET /center-codes/validate?code=CODE
// FIX: The worker now always returns JSON and flattens fields onto the root object.
// We surface valid, course_id, and any other row fields directly.
export const validateCenterCode = async (code) => {
    const result = await apiCall(`/center-codes/validate?code=${encodeURIComponent(code)}`);

    if (!result.success) {
        // Network/server error
        return { valid: false, message: result.error || 'حدث خطأ أثناء التحقق من الكود' };
    }

    const payload = result.data;

    // Worker returns { valid: false, message: '...' } for invalid codes
    if (!payload || payload.valid === false) {
        return { valid: false, message: payload?.message || 'كود غير صالح' };
    }

    // Worker returns the full row spread at root level when valid (valid: true, course_id, ...)
    // Normalise so callers always get { valid: true, course_id, ... }
    return {
        valid: true,
        ...payload,
        // Ensure course_id is always present at the top level
        course_id: payload.course_id || null,
    };
};

// Worker: POST /center-codes/use
export const useCenterCode = async (code, userId) => {
    return apiCall('/center-codes/use', {
        method: 'POST',
        body: JSON.stringify({ code, user_id: userId })
    });
};

export const deleteCenterCode = async (codeId) => {
    return apiCall(`/center-codes/${codeId}`, { method: 'DELETE' });
};

// ============= ENROLLMENTS =============
// Worker: GET /enrollments?user_id=
export const getEnrolledCourses = async (userId) => {
    const query = userId ? `?user_id=${userId}` : '';
    return apiCall(`/enrollments${query}`);
};

// Worker: GET /enrollments?course_id= — get all enrolled students for a specific course
export const getEnrolledStudentsByCourse = async (courseId) => {
    return apiCall(`/enrollments?course_id=${courseId}`);
};

export const getEnrollmentsByCourse = async (courseId) => {
    return apiCall('/enrollments?course_id=' + courseId);
};

// ============= QUIZZES BY COURSE =============
export const getQuizzesByCourse = async (courseId) => {
    const query = courseId ? `?course_id=${courseId}` : '';
    return apiCall(`/quizzes${query}`);
};

// ============= WRONG ANSWERS =============
// Worker: GET /wrong-answers?user_id=
export const getWrongAnswers = async (userId) => {
    const query = userId ? `?user_id=${userId}` : '';
    return apiCall(`/wrong-answers${query}`);
};

// Worker: POST /wrong-answers
export const saveWrongAnswer = async (wrongAnswerData) => {
    return apiCall('/wrong-answers', {
        method: 'POST',
        body: JSON.stringify(wrongAnswerData)
    });
};

// Worker: DELETE /wrong-answers/:id
export const deleteWrongAnswer = async (wrongAnswerId) => {
    return apiCall(`/wrong-answers/${wrongAnswerId}`, { method: 'DELETE' });
};

// ============= COMPLETED LECTURES / ASSIGNMENTS =============
// Worker: GET /assignments/completed?user_id=
export const getCompletedLectures = async (userId) => {
    const query = userId ? `?user_id=${userId}` : '';
    return apiCall(`/assignments/completed${query}`);
};

// Worker: POST /assignments
export const submitAssignment = async (userId, lectureId) => {
    return apiCall('/assignments', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, lecture_id: lectureId })
    });
};

// ============= QUIZ ATTEMPTS (1-attempt enforcement) =============
// Check if a specific user has already attempted a specific quiz
export const getQuizAttempt = async (userId, quizId) => {
    return apiCall(`/quiz-attempts?user_id=${userId}&quiz_id=${quizId}`);
};

// Get all attempts for a user (for the Exams tab)
export const getMyQuizAttempts = async (userId) => {
    return apiCall(`/quiz-attempts?user_id=${userId}`);
};

// Save a new quiz attempt
export const saveQuizAttempt = async (attemptData) => {
    return apiCall('/quiz-attempts', {
        method: 'POST',
        body: JSON.stringify(attemptData)
    });
};

// Worker: DELETE /enrollments/:id
export const revokeEnrollment = async (enrollmentId) => {
    return apiCall(`/enrollments/${enrollmentId}`, { method: 'DELETE' });
};

// ============= VIDEO STREAM (Piped API) =============
export const getVideoStream = async (youtubeId) => {
    // A fresh list of working Piped instances for the next few days
    const PIPED_INSTANCES = [
        'https://pipedapi.kavin.rocks',
        'https://api.piped.privacydev.net',
        'https://pipedapi.smnz.de',
        'https://pipedapi.tokhmi.xyz',
        'https://piped-api.lunar.icu',
        'https://pipedapi.syncpundit.io',
        'https://pipedapi.darkness.services',
        'https://pipedapi.frontendfriendly.xyz'
    ];

    for (const baseUrl of PIPED_INSTANCES) {
        try {
            // Add a short timeout so it fails fast and moves to the next instance
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3500);
            
            const response = await fetch(`${baseUrl}/streams/${youtubeId}`, {
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (!response.ok) continue;
            const data = await response.json();
            if (data && data.hls) {
                return { success: true, hls: data.hls };
            }
        } catch (err) {
            continue; // Move to the next instance if this one is dead or timed out
        }
    }
    return { success: false, error: 'All Piped instances failed' };
};

// Worker: PUT /users/:id/points
export const addStudentPoints = async (userId, points) => {
    return apiCall(`/users/${userId}/points`, { method: 'PUT', body: JSON.stringify({ points }) });
};

// Worker: PUT /users/:id/reset-password
export const resetUserPassword = async (userId, newPassword) => {
    return apiCall(`/users/${userId}/reset-password`, {
        method: 'PUT',
        body: JSON.stringify({ newPassword })
    });
};

// Worker: PUT /users/:id/reset-device
export const resetDeviceBinding = async (userId) => {
    return apiCall(`/users/${userId}/reset-device`, { method: 'PUT' });
};

// ============= LEADERBOARD TOGGLE =============
export const toggleLeaderboard = async (userId, action) => {
    return apiCall('/users/leaderboard', {
        method: 'POST',
        body: JSON.stringify({ userId, action })
    });
};

// ============= FILE UPLOAD (R2) =============
// Upload file to backend (multipart/form-data — bypasses apiCall to avoid JSON Content-Type)
export const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    try {
        const response = await fetch(`${API_BASE_URL}/upload`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: formData,
        });
        const data = await response.json();
        return data;
    } catch (error) {
        return { success: false, error: error.message };
    }
};
